import { computed, DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import { PrimeNG } from 'primeng/config';
import { UserPreferencesService } from '@core/services/user-preferences.service';
import {
  ACCENT_PRESETS,
  AccentId,
  AccentPreset,
  APPEARANCE_DEFAULTS,
  AppearancePrefs,
  BackgroundId,
  FONT_PRESETS,
  FONT_PREVIEW_WEIGHT,
  FontId,
  fontStack,
  GlassId,
  isAccentId,
  isBackgroundId,
  isFontId,
  isGlassId,
  isPatternId,
  isThemeChoice,
  LEGACY_ACCENT_MAP,
  LEGACY_PATTERN_MAP,
  PatternId,
  ThemeChoice,
} from 'app/shared/appearance/appearance-presets';
import { accentCssVariables, primePalette } from 'app/shared/appearance/accent-color';

/** Light or dark, after `system` is resolved against the operating system. */
export type ResolvedScheme = 'light' | 'dark';

/** localStorage key of the saved appearance (shared with the pre-redesign model). */
const PREFS_KEY = 'theme-preferences';
/** Key of the appearance inside `UserPreferencesService`. */
const USER_PREFS_FIELD = 'theme';
/** Version stamped on the saved shape; anything else is read as the old (v1) shape. */
const STORAGE_VERSION = 2;

/** What is written to storage: the six fields plus the shape version. */
interface StoredAppearance extends AppearancePrefs {
  v: typeof STORAGE_VERSION;
}

/** The six fields, in the order Preferences lists them. */
const APPEARANCE_FIELDS = [
  'theme',
  'accent',
  'background',
  'pattern',
  'glass',
  'font',
] as const satisfies readonly (keyof AppearancePrefs)[];

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/** Marks the font `<link>`s `ensureFont` adds (MeetingRec's `fontLoader.ts`). */
const FONT_LINK_ATTRIBUTE = 'data-appearance-font';
/** Weights downloaded for the applied font. */
const FONT_WEIGHTS = [400, 500, 600, 700] as const;
/** Weights a preview downloads: regular too, since a few families have no 500. */
const FONT_PREVIEW_WEIGHTS = [400, FONT_PREVIEW_WEIGHT] as const;

const PRIME_THEME_OPTIONS = {
  darkModeSelector: '.dark',
  cssLayer: { name: 'primeng', order: 'tailwind, primeng' },
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The six fields of a saved v2 shape, each falling back to its default when invalid. */
function readCurrentShape(raw: Record<string, unknown>): AppearancePrefs {
  return {
    theme: isThemeChoice(raw['theme']) ? raw['theme'] : APPEARANCE_DEFAULTS.theme,
    accent: isAccentId(raw['accent']) ? raw['accent'] : APPEARANCE_DEFAULTS.accent,
    background: isBackgroundId(raw['background'])
      ? raw['background']
      : APPEARANCE_DEFAULTS.background,
    pattern: isPatternId(raw['pattern']) ? raw['pattern'] : APPEARANCE_DEFAULTS.pattern,
    glass: isGlassId(raw['glass']) ? raw['glass'] : APPEARANCE_DEFAULTS.glass,
    font: isFontId(raw['font']) ? raw['font'] : APPEARANCE_DEFAULTS.font,
  };
}

/**
 * The old (v1) shape `{ mode, light: {theme, accent}, dark: {…}, backgroundPattern,
 * fontFamily }` mapped onto the new model: the custom themes collapse to `mode`, the
 * light-mode accent and the pattern go through the legacy maps, the font is kept when
 * it is still in the catalog. Background and glass did not exist, so they get defaults.
 */
function migrateLegacyShape(raw: Record<string, unknown>): AppearancePrefs {
  const lightPrefs = isRecord(raw['light']) ? raw['light'] : {};
  const legacyAccent = lightPrefs['accent'];
  const legacyPattern = raw['backgroundPattern'];
  const accent = typeof legacyAccent === 'string' ? LEGACY_ACCENT_MAP[legacyAccent] : undefined;
  const pattern = typeof legacyPattern === 'string' ? LEGACY_PATTERN_MAP[legacyPattern] : undefined;
  return {
    theme: isThemeChoice(raw['mode']) ? raw['mode'] : APPEARANCE_DEFAULTS.theme,
    // The guards also reject inherited keys (`LEGACY_ACCENT_MAP['constructor']`).
    accent: isAccentId(accent) ? accent : APPEARANCE_DEFAULTS.accent,
    background: APPEARANCE_DEFAULTS.background,
    pattern: isPatternId(pattern) ? pattern : APPEARANCE_DEFAULTS.pattern,
    glass: APPEARANCE_DEFAULTS.glass,
    font: isFontId(raw['fontFamily']) ? raw['fontFamily'] : APPEARANCE_DEFAULTS.font,
  };
}

/** Any stored value (v2, v1 or garbage) as a complete, valid appearance. */
function normalizeAppearance(raw: unknown): AppearancePrefs {
  if (!isRecord(raw)) return { ...APPEARANCE_DEFAULTS };
  return raw['v'] === STORAGE_VERSION ? readCurrentShape(raw) : migrateLegacyShape(raw);
}

function accentPresetOf(id: AccentId): AccentPreset {
  return (
    ACCENT_PRESETS.find((preset) => preset.id === id) ??
    ACCENT_PRESETS.find((preset) => preset.id === APPEARANCE_DEFAULTS.accent)
  );
}

function fontPresetOf(id: FontId) {
  return (
    FONT_PRESETS.find((preset) => preset.id === id) ??
    FONT_PRESETS.find((preset) => preset.id === APPEARANCE_DEFAULTS.font)
  );
}

/** The Google Fonts css2 URL for a family at the given weights. */
function googleFontsHref(googleFamily: string, weights: readonly number[]): string {
  const family = googleFamily.replace(/ /g, '+');
  return `https://fonts.googleapis.com/css2?family=${family}:wght@${weights.join(';')}&display=swap`;
}

/**
 * Owner of the app's appearance (Preferences → Appearance): theme, accent colour,
 * background, texture, glass and font. Applies them on `<html>` (scheme class, accent
 * CSS variables, data attributes, `--font-sans`) and to PrimeNG, and persists them.
 *
 * The Preferences page edits inside a draft (`beginDraft`): every setter still applies
 * live — that is the preview — but nothing is saved until `commitDraft`, and
 * `discardDraft` puts the saved appearance back. With no draft open, setters save
 * immediately (the user menu's light/dark switch, for instance).
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly _primeNG = inject(PrimeNG);
  private readonly _userPrefs = inject(UserPreferencesService);
  private readonly _destroyRef = inject(DestroyRef);

  /** What is applied right now: the open draft, or the saved appearance. */
  private readonly _live = signal<AppearancePrefs>({ ...APPEARANCE_DEFAULTS });
  /** What is persisted. Equals `_live` whenever no draft is open. */
  private readonly _saved = signal<AppearancePrefs>({ ...APPEARANCE_DEFAULTS });
  /** The operating system currently prefers dark. */
  private readonly _systemPrefersDark = signal(false);

  private _draftOpen = false;
  /** Accent whose PrimeNG preset is installed; the preset is rebuilt only when it changes. */
  private _primeAccent: AccentId | null = null;
  /** CSS variables the last accent wrote on `<html>`, cleared before the next one. */
  private _appliedAccentVariables: string[] = [];

  /** Light/dark choice (`system` follows the OS). */
  readonly theme: Signal<ThemeChoice> = computed(() => this._live().theme);
  readonly accent: Signal<AccentId> = computed(() => this._live().accent);
  readonly background: Signal<BackgroundId> = computed(() => this._live().background);
  readonly pattern: Signal<PatternId> = computed(() => this._live().pattern);
  readonly glass: Signal<GlassId> = computed(() => this._live().glass);
  readonly font: Signal<FontId> = computed(() => this._live().font);

  /** The scheme actually painted: `theme`, with `system` resolved against the OS. */
  readonly resolvedScheme: Signal<ResolvedScheme> = computed(() => {
    const theme = this.theme();
    if (theme !== 'system') return theme;
    return this._systemPrefersDark() ? 'dark' : 'light';
  });

  /** How many of the six fields differ from what is saved (0 without a draft). */
  readonly pendingCount: Signal<number> = computed(() => {
    const live = this._live();
    const saved = this._saved();
    return APPEARANCE_FIELDS.filter((field) => live[field] !== saved[field]).length;
  });

  /** The open draft differs from the saved appearance. */
  readonly draftDirty: Signal<boolean> = computed(() => this.pendingCount() > 0);

  /** Light/dark/system choice, for consumers of the pre-redesign API. Same as `theme`. */
  readonly currentMode: Signal<ThemeChoice> = this.theme;

  constructor() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const saved = this._readSaved();
    this._saved.set(saved);
    this._live.set(saved);
    this._listenToSystemScheme();
    this._applyToDocument();
  }

  // ====================================================================
  // Public API
  // ====================================================================

  /** Sets the light/dark choice. */
  setTheme(theme: ThemeChoice): void {
    if (isThemeChoice(theme)) this._change({ theme });
  }

  /** Sets the accent colour. */
  setAccent(accent: AccentId): void {
    if (isAccentId(accent)) this._change({ accent });
  }

  /** Sets the mesh background (`none` for the plain page). */
  setBackground(background: BackgroundId): void {
    if (isBackgroundId(background)) this._change({ background });
  }

  /** Sets the texture laid over the background (`none` included). */
  setPattern(pattern: PatternId): void {
    if (isPatternId(pattern)) this._change({ pattern });
  }

  /** Turns the frosted-glass chrome on or off. */
  setGlass(glass: GlassId): void {
    if (isGlassId(glass)) this._change({ glass });
  }

  /** Sets the app font. */
  setFont(font: FontId): void {
    if (isFontId(font)) this._change({ font });
  }

  /** The user menu's light/dark/auto switch. Alias of `setTheme`, drafted like any setter. */
  switchBaseMode(mode: ThemeChoice): void {
    this.setTheme(mode);
  }

  /** Starts a draft: later changes preview live but are not saved until `commitDraft`. */
  beginDraft(): void {
    this._draftOpen = true;
  }

  /** Saves the draft's appearance and closes it. No-op without a draft. */
  commitDraft(): void {
    if (!this._draftOpen) return;
    this._draftOpen = false;
    this._save(this._live());
  }

  /** Throws the draft away and re-applies the saved appearance. No-op without a draft. */
  discardDraft(): void {
    if (!this._draftOpen) return;
    this._draftOpen = false;
    this._live.set(this._saved());
    this._applyToDocument();
  }

  /**
   * Adds the Google Fonts stylesheet for `font` to `<head>` unless it is already there
   * (one `<link data-appearance-font="<id>">` per font, `<id>@500` for a preview).
   * `preview` downloads only the weights the picker tiles need. No-op for fonts that
   * download nothing (the system font).
   */
  ensureFont(font: FontId, preview = false): void {
    if (typeof document === 'undefined') return;
    const preset = FONT_PRESETS.find((candidate) => candidate.id === font);
    if (!preset?.google) return;
    const marker = preview ? `${preset.id}@${FONT_PREVIEW_WEIGHT}` : preset.id;
    const existing = Array.from(document.head.querySelectorAll(`link[${FONT_LINK_ATTRIBUTE}]`));
    if (existing.some((link) => link.getAttribute(FONT_LINK_ATTRIBUTE) === marker)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = googleFontsHref(preset.google, preview ? FONT_PREVIEW_WEIGHTS : FONT_WEIGHTS);
    link.setAttribute(FONT_LINK_ATTRIBUTE, marker);
    document.head.appendChild(link);
  }

  // ====================================================================
  // Private — changes and persistence
  // ====================================================================

  /** Applies a change live; saves it too unless a draft is open. */
  private _change(partial: Partial<AppearancePrefs>): void {
    const next = { ...this._live(), ...partial };
    this._live.set(next);
    this._applyToDocument();
    if (!this._draftOpen) this._save(next);
  }

  private _save(prefs: AppearancePrefs): void {
    this._saved.set({ ...prefs });
    const stored: StoredAppearance = { v: STORAGE_VERSION, ...prefs };
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(stored));
    } catch {
      // Storage blocked (private mode, quota): the appearance stays applied for this
      // session and `_saved` still drives the draft state; there is nothing to retry.
    }
    this._userPrefs.patch({ [USER_PREFS_FIELD]: stored }).subscribe({
      error: () => {
        // The localStorage copy written above is the fallback; nothing else to do.
      },
    });
  }

  /** The saved appearance: the user-preferences store first, then the local key. */
  private _readSaved(): AppearancePrefs {
    try {
      const fromUserPrefs = this._userPrefs.get<unknown>(USER_PREFS_FIELD);
      if (fromUserPrefs) return normalizeAppearance(fromUserPrefs);
      const raw = localStorage.getItem(PREFS_KEY);
      return raw ? normalizeAppearance(JSON.parse(raw)) : { ...APPEARANCE_DEFAULTS };
    } catch {
      // Unreadable storage or malformed JSON: boot on the defaults.
      return { ...APPEARANCE_DEFAULTS };
    }
  }

  // ====================================================================
  // Private — applying to the document
  // ====================================================================

  private _applyToDocument(): void {
    if (typeof document === 'undefined') return;
    const prefs = this._live();
    const scheme = this.resolvedScheme();
    const root = document.documentElement;
    const accent = accentPresetOf(prefs.accent);
    this._applyScheme(root, scheme);
    this._applyAccentVariables(root, accent, scheme);
    this._applyPrimeAccent(accent);
    this._applyDataAttributes(root, prefs);
    this._applyFont(root, prefs.font);
  }

  /** `.dark`/`.light` on `<html>`, mirrored on `<body>` for the legacy theme tokens. */
  private _applyScheme(root: HTMLElement, scheme: ResolvedScheme): void {
    root.classList.remove('light', 'dark');
    root.classList.add(scheme);
    // legacy-theme.css scopes its tokens by body.light/body.dark; a stale `light` there
    // keeps the shell (body behind the floating sidebar gutter) in light colours.
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(scheme);
  }

  /**
   * Writes the accent's CSS variables inline with `!important` on `<html>`, so they win
   * over every stylesheet default (the same mechanism the app has always used).
   */
  private _applyAccentVariables(
    root: HTMLElement,
    accent: AccentPreset,
    scheme: ResolvedScheme
  ): void {
    const variables = accentCssVariables(accent, scheme);
    this._appliedAccentVariables.forEach((name) => root.style.removeProperty(name));
    Object.entries(variables).forEach(([name, value]) =>
      root.style.setProperty(name, value, 'important')
    );
    this._appliedAccentVariables = Object.keys(variables);
  }

  /** Installs the PrimeNG preset for the accent; both schemes live in one preset. */
  private _applyPrimeAccent(accent: AccentPreset): void {
    if (this._primeAccent === accent.id) return;
    this._primeAccent = accent.id as AccentId;
    const preset = definePreset(Lara, {
      semantic: {
        colorScheme: {
          light: { primary: primePalette(accent, 'light') },
          dark: { primary: primePalette(accent, 'dark') },
        },
      },
    });
    this._primeNG.theme.set({ preset, options: PRIME_THEME_OPTIONS });
  }

  /**
   * `data-background` and `data-glass` only when they differ from the default (the CSS
   * gates on their presence); `data-pattern` always, `none` included.
   */
  private _applyDataAttributes(root: HTMLElement, prefs: AppearancePrefs): void {
    this._toggleAttribute(
      root,
      'data-background',
      prefs.background,
      APPEARANCE_DEFAULTS.background
    );
    this._toggleAttribute(root, 'data-glass', prefs.glass, APPEARANCE_DEFAULTS.glass);
    root.setAttribute('data-pattern', prefs.pattern);
  }

  private _toggleAttribute(
    root: HTMLElement,
    name: string,
    value: string,
    defaultValue: string
  ): void {
    if (value === defaultValue) root.removeAttribute(name);
    else root.setAttribute(name, value);
  }

  /** Loads the font's full weights and points `--font-sans` at its stack. */
  private _applyFont(root: HTMLElement, font: FontId): void {
    const preset = fontPresetOf(font);
    this.ensureFont(preset.id as FontId);
    root.style.setProperty('--font-sans', fontStack(preset));
  }

  // ====================================================================
  // Private — system scheme
  // ====================================================================

  /** Keeps `system` following the OS live; the listener goes with the injector. */
  private _listenToSystemScheme(): void {
    const mediaQuery = window.matchMedia?.(DARK_SCHEME_QUERY);
    if (!mediaQuery) return;
    this._systemPrefersDark.set(mediaQuery.matches);
    const onChange = (event: MediaQueryListEvent) => {
      this._systemPrefersDark.set(event.matches);
      if (this.theme() === 'system') this._applyToDocument();
    };
    mediaQuery.addEventListener('change', onChange);
    this._destroyRef.onDestroy(() => mediaQuery.removeEventListener('change', onChange));
  }
}
